/**
 * Next-wizard draft API klient. Speiler V2.Wizard-kontraktene:
 *   POST   /api/v2/wizard/draft                            — opprett draft (body: { mode, fileConfig? })
 *   GET    /api/v2/wizard/draft/{publicId}                 — hent draft
 *   DELETE /api/v2/wizard/draft/{publicId}                 — slett draft
 *   PUT    /api/v2/wizard/draft/{publicId}/table           — sett tabell
 *   PUT    /api/v2/wizard/draft/{publicId}/import-def      — sett import-definisjon
 *   PUT    /api/v2/wizard/draft/{publicId}/upload          — sett file-config / upload
 *   PUT    /api/v2/wizard/draft/{publicId}/column-mapping  — sett kolonne-mapping
 *   PUT    /api/v2/wizard/draft/{publicId}/validation      — sett valideringsregler
 *   POST   /api/v2/wizard/draft/{publicId}/dry-run         — prøvekjøring
 *   POST   /api/v2/wizard/draft/{publicId}/execute         — execute
 *
 * Hver PUT/GET returnerer hele oppdaterte `WizardDraftResponse` med
 * `status` + `nextStep` slik at FE kan gate UI uten on-demand validering.
 *
 * Eksporterer `__setHttpClient` så tester kan injisere en stub uten å gå via
 * axios-proxyet (som lazy-loader auth/projectId).
 */

import type { AxiosInstance } from 'axios';
import axiosClient from '@/api/axiosClient';
import type {
  WizardColumnMappingDraft,
  WizardDraftState,
  WizardDraftStatus,
  WizardDraftTable,
  WizardDuplicateResolution,
  WizardFileConfigDraft,
  WizardImportDefinitionDraft,
  WizardMode,
  WizardValidationRuleDraft,
} from '@/types/wizardDraft';

interface RequestConfig {
  headers?: Record<string, string>;
}

/** Minimal http-shape som dekker det modulen trenger — gjør test-injection trivielt. */
export interface WizardDraftHttpClient {
  post: <T = unknown>(url: string, body?: unknown, config?: RequestConfig) => Promise<{ data: T }>;
  get: <T = unknown>(url: string) => Promise<{ data: T }>;
  put: <T = unknown>(url: string, body?: unknown, config?: RequestConfig) => Promise<{ data: T }>;
  delete: <T = unknown>(url: string) => Promise<{ data: T }>;
}

const JSON_BODY: RequestConfig = { headers: { 'Content-Type': 'application/json' } };

let httpClient: WizardDraftHttpClient = axiosClient as unknown as WizardDraftHttpClient;

/** Test-hook: bytt ut http-klienten (typisk vi.fn-stub). */
export function __setHttpClient (client: WizardDraftHttpClient | null): void {
  httpClient = (client ?? (axiosClient as unknown as WizardDraftHttpClient));
}

/**
 * BE-flat response — speiler `WizardDraft`-recorden fra
 * `src/ICS.Contracts/Wizard/WizardDraft.cs`. Brukes direkte av storen
 * (ingen snapshot-wrapper).
 */
export interface WizardDraftResponse {
  draftId: string;
  publicId: string;
  mode?: string | null;
  createdAt?: string;
  updatedAt?: string;
  status?: WizardDraftStatus | string;
  nextStep?: string | null;
  /** WI 2257: filas faktiske kolonne-overskrifter — driver kolonne-mapping-steget. */
  fileColumns?: WizardDraftState['fileColumns'];
  table?: WizardDraftState['table'];
  fileConfig?: WizardDraftState['fileConfig'];
  importDefinition?: WizardDraftState['importDefinition'];
  validationRules?: WizardDraftState['validationRules'];
  columnMapping?: WizardDraftState['columnMapping'];
  setupFileAnalysis?: WizardDraftState['setupFileAnalysis'];
  /** WI #1362: meldinger fra parser når rader i `Valideringsregler`-arket
   *  ble hoppet over (ukjent regel-type, ukjent kolonne, mv.). */
  validationRuleWarnings?: WizardDraftState['validationRuleWarnings'];
  /** WI #1687: BE-eid per-steg-gating (steg → ufullstendig + årsak). */
  stepGates?: WizardDraftState['stepGates'];
}

export interface WizardExecuteSuccess {
  kind: 'success';
  tableKey: string | null;
  importDefinitionKey: string | null;
  uploadId: string | null;
}

/** Etter 2026-05-26: alle 422 fra execute kastes (vises som global danger-toast).
 *  Partial-result-konseptet er borte; WizardExecuteResult er kun success-shape. */
export type WizardExecuteResult = WizardExecuteSuccess;

const BASE = 'v2/wizard/draft';

/** Smalt opprett-body — kun mode (+ valgfri fileConfig). Full state bygges
 *  inkrementelt via per-artefakt PUTs. */
export interface CreateDraftBody {
  mode: WizardMode;
  fileConfig?: WizardFileConfigDraft | null;
}

export function createDraft (body: CreateDraftBody): Promise<WizardDraftResponse> {
  return httpClient
    .post<WizardDraftResponse>(BASE, body)
    .then((response) => response.data);
}

export function fetchDraft (publicId: string): Promise<WizardDraftResponse> {
  return httpClient
    .get<WizardDraftResponse>(`${BASE}/${encodeURIComponent(publicId)}`)
    .then((response) => response.data);
}

export function deleteDraft (publicId: string): Promise<void> {
  return httpClient
    .delete(`${BASE}/${encodeURIComponent(publicId)}`)
    .then(() => undefined);
}

export function putTable (
  publicId: string,
  value: WizardDraftTable | null,
): Promise<WizardDraftResponse> {
  return httpClient
    .put<WizardDraftResponse>(`${BASE}/${encodeURIComponent(publicId)}/table`, value, JSON_BODY)
    .then((response) => response.data);
}

export function putImportDefinition (
  publicId: string,
  value: WizardImportDefinitionDraft | null,
): Promise<WizardDraftResponse> {
  return httpClient
    .put<WizardDraftResponse>(`${BASE}/${encodeURIComponent(publicId)}/import-def`, value, JSON_BODY)
    .then((response) => response.data);
}

export function putUpload (
  publicId: string,
  value: WizardFileConfigDraft | null,
): Promise<WizardDraftResponse> {
  return httpClient
    .put<WizardDraftResponse>(`${BASE}/${encodeURIComponent(publicId)}/upload`, value, JSON_BODY)
    .then((response) => response.data);
}

export function putColumnMapping (
  publicId: string,
  value: WizardColumnMappingDraft | null,
): Promise<WizardDraftResponse> {
  return httpClient
    .put<WizardDraftResponse>(`${BASE}/${encodeURIComponent(publicId)}/column-mapping`, value, JSON_BODY)
    .then((response) => response.data);
}

export function putValidation (
  publicId: string,
  value: WizardValidationRuleDraft[],
): Promise<WizardDraftResponse> {
  return httpClient
    .put<WizardDraftResponse>(`${BASE}/${encodeURIComponent(publicId)}/validation`, value, JSON_BODY)
    .then((response) => response.data);
}

/**
 * Task 2583 (US 2524): valgfri execute-body — speiler BE-recorden
 * `WizardExecuteRequest` (ICS.ImportPipeline). Bærer brukerens duplikat-valg
 * for DENNE kjøringen; uten valg sendes execute uten body (historisk signatur).
 */
export interface WizardExecuteRequestBody {
  duplicateResolution: WizardDuplicateResolution;
}

/**
 * Execute draft. BE returnerer HTTP 200 + `WizardExecuteSuccess` ved suksess.
 * 4xx/5xx (inkludert 422 partial-failure) kastes til caller — wizardDraftStore.
 * execute() viser BE-meldingen som global danger-toast (memory
 * `never_swallow_be_errors` + smoketest 2.0 #8). Tidligere 422→partial-
 * konverteringen ble fjernet fordi den fikk UI til å late som om alt gikk
 * bra og navigere uten executionId.
 */
export function executeDraft (
  publicId: string,
  body?: WizardExecuteRequestBody,
): Promise<WizardExecuteResult> {
  const url = `${BASE}/${encodeURIComponent(publicId)}/execute`;
  const request = body === undefined
    ? httpClient.post<WizardExecuteResult>(url)
    : httpClient.post<WizardExecuteResult>(url, body, JSON_BODY);
  return request.then((response) => response.data);
}

/** Type-export så `axios` brukere kan injisere en eksisterende AxiosInstance. */
export type { AxiosInstance };
